import type { ManutencaoStatus } from "@/services/manutencao";
import { cn } from "@/utils/cn";

export const CARGOS = [
  "ADM",
  "Engenheiro de Manutenção",
  "Auxiliar de Engenharia",
  "Oficial de Manutenção 01",
  "Oficial de Manutenção 02",
  "Operador",
] as const;

export const CARGOS_EXECUTORES = ["Oficial de Manutenção 01", "Oficial de Manutenção 02", "Operador"];

export const CATEGORIAS: Array<{ nome: string; itens: string[] }> = [
  { nome: "HIDRÁULICA", itens: ["Vazamento", "Entupimento", "Registro", "Caixa d'água", "Esgoto"] },
  { nome: "ELÉTRICA", itens: ["Tomadas", "Interruptores", "Disjuntor", "Iluminação", "Chuveiro"] },
  { nome: "ESTRUTURAL", itens: ["Trinca", "Fissura", "Infiltração", "Piso", "Revestimento"] },
  { nome: "ESQUADRIAS", itens: ["Porta", "Janela", "Fechadura", "Vidro"] },
  { nome: "PINTURA", itens: ["Mancha", "Descascamento", "Retoque"] },
  { nome: "FORRO", itens: ["Forro → Fissura"] },
  { nome: "TELHADO", itens: ["Telhado → Infiltração"] },
  { nome: "TANQUE EXTERNO", itens: ["Tanque Externo → Fixação"] },
  { nome: "OUTROS", itens: ["Limpeza", "Cobertura", "Área externa", "Não classificado"] },
];

export const STATUS_LABEL: Record<ManutencaoStatus, string> = {
  agendado: "Agendado",
  atendimento: "Em andamento",
  concluido: "Concluído",
  cancelado: "Cancelado",
  ausente: "Cliente ausente",
  parcial: "Parcialmente concluído",
  reagendado: "Reagendar",
};

const STATUS_CLASS: Record<ManutencaoStatus, string> = {
  agendado: "bg-amber-100 text-amber-800",
  atendimento: "bg-sky-100 text-sky-800",
  concluido: "bg-emerald-100 text-emerald-800",
  cancelado: "bg-red-100 text-red-800",
  ausente: "bg-orange-100 text-orange-800",
  parcial: "bg-blue-100 text-blue-800",
  reagendado: "bg-yellow-100 text-yellow-800",
};

export const OCORRENCIAS = [
  "Serviço realizado normalmente",
  "Cliente não se encontra na residência",
  "Serviço parcialmente realizado",
  "Material necessário não disponível",
  "Cliente solicitou reagendamento",
  "Outra ocorrência",
];

export function StatusBadge({ status }: { status: ManutencaoStatus }) {
  return (
    <span className={cn("inline-flex rounded-full px-2.5 py-1 text-xs font-semibold", STATUS_CLASS[status])}>
      {STATUS_LABEL[status]}
    </span>
  );
}

const ETAPAS_ACOMPANHAMENTO = ["Agendado", "Em andamento", "Concluído"] as const;

/** Etapa da trilha. Cancelado e cliente ausente ficam fora dela. */
export function etapaAcompanhamento(status: ManutencaoStatus): 0 | 1 | 2 | null {
  if (status === "agendado" || status === "reagendado") return 0;
  if (status === "atendimento" || status === "parcial") return 1;
  if (status === "concluido") return 2;
  return null;
}

export function Acompanhamento({ status }: { status: ManutencaoStatus }) {
  const etapa = etapaAcompanhamento(status);
  if (etapa == null) return null;
  return (
    <ol className="mt-2 flex items-center gap-1.5" aria-label="Acompanhamento da manutenção">
      {ETAPAS_ACOMPANHAMENTO.map((nome, index) => {
        const feita = index < etapa;
        const atual = index === etapa;
        return (
          <li key={nome} className="flex min-w-0 flex-1 items-center gap-1.5">
            <span
              className={cn(
                "flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold",
                atual && "bg-brand-600 text-white",
                feita && "bg-emerald-600 text-white",
                !atual && !feita && "bg-slate-200 text-slate-500",
              )}
            >
              {index + 1}
            </span>
            <span
              className={cn(
                "truncate text-xs font-semibold",
                atual && "text-brand-800",
                feita && "text-emerald-800",
                !atual && !feita && "text-slate-400",
              )}
            >
              {nome}
            </span>
            {index < ETAPAS_ACOMPANHAMENTO.length - 1 && (
              <span className={cn("h-0.5 min-w-2 flex-1 rounded", index < etapa ? "bg-emerald-500" : "bg-slate-200")} />
            )}
          </li>
        );
      })}
    </ol>
  );
}

export function isoDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function parseIso(value: string): Date {
  const [y, m, d] = value.split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

export function mondayOf(date: Date): Date {
  const copy = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const offset = (copy.getDay() + 6) % 7;
  copy.setDate(copy.getDate() - offset);
  return copy;
}

export function weekDays(anchor: Date): string[] {
  const start = mondayOf(anchor);
  return Array.from({ length: 7 }, (_, i) => {
    const day = new Date(start);
    day.setDate(start.getDate() + i);
    return isoDate(day);
  });
}

export function formatDay(value: string): string {
  return parseIso(value).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
}

export function formatWeekLabel(days: string[]): string {
  const a = parseIso(days[0]).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
  const b = parseIso(days[6]).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });
  return `${a} — ${b}`;
}

export const WEEKDAY_SHORT = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

export function fieldClass(): string {
  return "w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100";
}

/** "01" vira "Casa 01". Um nome já escrito (Casa 01, Apto 12, Bloco B) permanece. */
export function casaLabel(value: string): string {
  const nome = value.trim();
  if (!nome) return "—";
  if (/^\d+$/.test(nome)) return `Casa ${nome.padStart(2, "0")}`;
  return nome;
}

/** "5" vira "Quadra 5". "QD 162" e "Quadra B" permanecem. */
export function quadraLabel(value: string): string {
  const nome = value.trim();
  if (!nome) return "Quadra —";
  if (/^(quadra\b|qd)/i.test(nome)) return nome;
  return `Quadra ${nome}`;
}

export function enderecoAgendamento(casa: string, quadra: string): string {
  const nomeCasa = casa.trim() ? casaLabel(casa) : "Casa —";
  return `${nomeCasa} · ${quadraLabel(quadra)}`;
}
